import React, { forwardRef, ForwardRefRenderFunction, useImperativeHandle, useState } from "react";

import { Box } from "@mui/material";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FiMail } from "react-icons/fi";
import { useHistory } from "react-router-dom";
import * as yup from "yup";

import Button, { Color } from "../../../../../components/Form/Button";
import Input from "../../../../../components/Form/Input";
import { validateForm } from "../../actions";
import useStyles from "./styles";

interface User {
  id: string | number;
  email: string;
}
interface HistoryState {
  token: string;
  user: User;
}

interface ForgotPasswordComponentProps {
  handleSubmit?: (event: React.FormEvent, tokenIsExpired?: boolean) => void;
  emailSent?: boolean;
  tokenIsExpired?: boolean;
  isLoading?: boolean;
}

export interface ForgotPasswordHandle {
  email: string;
  setEmail: (email: string) => void;
}

const ForgotPasswordComponent: ForwardRefRenderFunction<
  ForgotPasswordHandle,
  ForgotPasswordComponentProps
> = (props, ref) => {
  const { emailSent, tokenIsExpired, isLoading, handleSubmit } = props;
  const history = useHistory<HistoryState>();
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const fieldHasError = (field: string) => !!errors.find((error) => error.path === field);
  const getErrorByField = (field: string) => errors.find((error) => error.path === field)?.message;

  useImperativeHandle(ref, () => ({
    email,
    setEmail,
  }));

  const handleSubmitForm = (event: React.FormEvent, tokenIsExpired?: boolean) => {
    event.preventDefault();
    if (validateForm(translate, email, setErrors)) {
      if (handleSubmit) handleSubmit(event, tokenIsExpired);
    }
  };

  const renderContent = () => {
    if (emailSent) {
      return (
        <Stack
          className={classes.mainGrid}
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={3}
        >
          <h2 className={classes.title}>{translate("pleaseCheckYourEmail")}</h2>
          <Typography
            fontFamily="Manrope"
            fontWeight={400}
            fontSize="14px"
            lineHeight="20px"
            color="#667085"
          >
            {translate("ifAMatchingAccountWasFoundAnEmailWasSentTo")}
            <b>{` ${email} `}</b>
            {translate("toAllowYourPasswordReset")}
          </Typography>
          <span className={classes.text}>
            {translate("didnTReceiveTheEmail")}
            <span
              className={classes.clickableText}
              onClick={handleSubmitForm}
              aria-label="forgotPasswordResendLink"
              data-cy="forgotPasswordResendLink"
            >
              {translate("clickToResend")}
            </span>
          </span>
          <Button
            fullWidth
            customColor={Color.BRAND}
            onClick={() => history.push("/signin")}
            aria-label="forgotPasswordBackToLoginButton"
            data-cy="forgotPasswordBackToLoginButton"
          >
            {translate("backToLogin")}
          </Button>
        </Stack>
      );
    }

    if (tokenIsExpired) {
      return (
        <Stack
          className={classes.mainGrid}
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={4}
        >
          <Stack spacing={1}>
            <Typography
              fontFamily="Manrope"
              fontWeight={700}
              fontSize="24px"
              lineHeight="32px"
              color="#DC3545"
            >
              {translate("sorryTheLinkHasExpired")}
            </Typography>
            <Typography
              fontFamily="Manrope"
              fontWeight={400}
              fontSize="14px"
              lineHeight="20px"
              color="#667085"
            >
              {translate(
                "theResetPasswordLinkHasExpiredYouCanRequestANewOneByClickingOnTheButtonBelow"
              )}
            </Typography>
          </Stack>
          <Button
            fullWidth
            customColor={Color.BRAND}
            onClick={(e) => handleSubmitForm(e, tokenIsExpired)}
            disabled={!email}
            loading={isLoading}
            aria-label="resendPasswordLink"
            data-cy="forgotPasswordResetLinkButton"
          >
            {translate("resetPasswordLink")}
          </Button>
        </Stack>
      );
    }

    return (
      <Stack
        className={classes.mainGrid}
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={3}
      >
        <h2 className={classes.title}>{translate("resetPassword")}</h2>
        <span aria-label="enterTheEmailAddress" className={classes.text}>
          {translate(
            "enterTheEmailAddressAssociatedWithYourAccountAndWeLlSendYouALinkToResetYourPassword"
          )}
        </span>
        <form className="w-100" onSubmit={handleSubmitForm}>
          <Stack width="100%" justifyContent="center" alignItems="flex-start" spacing={3}>
            <Input
              fullWidth
              required
              label={translate("email")}
              value={email}
              name="user-mail"
              id="user-mail"
              disabled={isLoading}
              placeholder={translate("sampleEmailCom")}
              onChange={(event) => setEmail(event.target.value)}
              error={fieldHasError("email")}
              errorMessage={getErrorByField("email")}
              rightIcon={<FiMail />}
              data-cy="forgotPasswordEmail"
            />
            <Button
              type="submit"
              fullWidth
              customColor={Color.BRAND}
              loading={isLoading}
              aria-label="buttonSubmit"
              data-cy="forgotPasswordResetButton"
            >
              {translate("reset")}
            </Button>
          </Stack>
        </form>
        <div className={classes.returnContainer}>
          <span
            className={classes.clickableText}
            onClick={() => history.push("/signin")}
            data-cy="returnSignInLink"
            aria-label="returnSignInLink"
          >
            {translate("returnToLogin")}
          </span>
        </div>
      </Stack>
    );
  };

  return (
    <Box className={classes.boxContainer}>
      {renderContent()}
    </Box>
  );
};

export default forwardRef(ForgotPasswordComponent);
